import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDataCache, PATIENT_LIST_KEY } from '../../context/DataCacheContext';
import api, {
  patientAPI,
  departmentAPI,
  billingAPI,
  pharmacyAPI,
  laboratoryAPI,
  ipdAPI,
  inventoryAPI,
  organizationAPI,
  maternalAPI,
  emergencyAPI,
  radiologyAPI,
  proceduresAPI,
  financeAPI,
  bloodBankAPI,
  familyPlanningAPI,
  appointmentAPI,
  followupAPI,
  clinicalAPI,
  diagnosisAPI,
  authAPI
} from '../../services/api';

const ENDPOINT_LABEL_RULES = [
  { match: '/api/organization/settings', label: 'Loading Organization Settings...' },
  { match: '/api/organization/setup-status', label: 'Loading Setup Status...' },
  { match: '/api/departments', label: 'Loading Departments...' },
  { match: '/api/auth/me/permissions', label: 'Loading User Permissions...' },
  { match: '/api/auth/permissions/modules', label: 'Loading Permission Modules...' },
  { match: '/api/payments/subscriptions/active', label: 'Loading Subscription Status...' },
  { match: '/api/demo/status-by-user', label: 'Loading Account Status...' },
  { match: '/api/clinical-parameters', label: 'Loading Clinical Parameters...' },
  { match: '/api/patients/relationship-types', label: 'Loading Relationship Types...' },
  { match: '/api/patients/registration-limit', label: 'Loading Registration Limits...' },
  { match: '/api/patients', label: 'Loading Patients...' },
  { match: '/api/appointments', label: 'Loading Appointments...' },
  { match: '/api/auth/users', label: 'Loading Staff Users...' },
  { match: '/api/followups', label: 'Loading Follow-ups...' },
  { match: '/api/clinical/visits', label: 'Loading Clinical Visits...' },
  { match: '/api/diagnosis-catalog', label: 'Loading Diagnosis Catalog...' },
  { match: '/api/pharmacy/drugs/catalog', label: 'Loading Drug Catalog...' },
  { match: '/api/pharmacy/drugs', label: 'Loading Pharmacy Drugs...' },
  { match: '/api/pharmacy/prescriptions', label: 'Loading Pending Prescriptions...' },
  { match: '/api/laboratory/tests', label: 'Loading Lab Tests...' },
  { match: '/api/laboratory/orders', label: 'Loading Lab Orders...' },
  { match: '/api/billing/services', label: 'Loading Service Catalog...' },
  { match: '/api/billing/invoices', label: 'Loading Invoices...' },
  { match: '/api/ipd/medications/due', label: 'Loading Medication Alerts...' },
  { match: '/api/ipd/wards', label: 'Loading Wards...' },
  { match: '/api/ipd/beds', label: 'Loading Beds...' },
  { match: '/api/ipd/admissions', label: 'Loading Admissions...' },
  { match: '/api/maternal/postnatal/due-medications', label: 'Loading Postnatal Alerts...' },
  { match: '/api/maternal/stats', label: 'Loading Maternal Stats...' },
  { match: '/api/maternal/pregnancies', label: 'Loading Pregnancies...' },
  { match: '/api/maternal/children', label: 'Loading Children...' },
  { match: '/api/maternal/anc-visits', label: 'Loading ANC Visits...' },
  { match: '/api/maternal/deliveries', label: 'Loading Deliveries...' },
  { match: '/api/maternal/postnatal-visits', label: 'Loading Postnatal Visits...' },
  { match: '/api/maternal/immunizations', label: 'Loading Immunizations...' },
  { match: '/api/family-planning/clients', label: 'Loading FP Clients...' },
  { match: '/api/family-planning/methods', label: 'Loading FP Methods...' },
  { match: '/api/blood-bank/donors', label: 'Loading Blood Donors...' },
  { match: '/api/blood-bank/inventory', label: 'Loading Blood Inventory...' },
  { match: '/api/emergency/triage', label: 'Loading Triages...' },
  { match: '/api/emergency/rooms', label: 'Loading ED Rooms...' },
  { match: '/api/emergency/visits', label: 'Loading Emergency Visits...' },
  { match: '/api/emergency/stats', label: 'Loading Emergency Stats...' },
  { match: '/api/radiology/modalities', label: 'Loading Imaging Modalities...' },
  { match: '/api/radiology/studies', label: 'Loading Imaging Studies...' },
  { match: '/api/procedures/catalog', label: 'Loading Procedure Catalog...' },
  { match: '/api/procedures/theatres', label: 'Loading Theatres...' },
  { match: '/api/procedures/orders', label: 'Loading Procedure Orders...' },
  { match: '/api/inventory/stock-count-due', label: 'Loading Stock Count Status...' },
  { match: '/api/inventory/all-items', label: 'Loading Inventory Items...' },
  { match: '/api/inventory/suppliers', label: 'Loading Suppliers...' },
  { match: '/api/inventory/warehouses', label: 'Loading Warehouses...' },
  { match: '/api/finance/accounts', label: 'Loading Accounts...' },
  { match: '/api/notifications', label: 'Loading Notifications...' },
  { match: '/api/chat/unread-summary', label: 'Loading Chat Messages...' },
  { match: '/api/superadmin/simulate-plan', label: 'Loading Plan Configuration...' },
  { match: '/api/workforce', label: 'Loading Duty Roster...' },
];

const getRequestLabel = (url = '') => {
  if (!url) return null;
  if (url.includes('/api/track/') || url.includes('/health')) return null;
  for (const rule of ENDPOINT_LABEL_RULES) {
    if (url.includes(rule.match)) return rule.label;
  }
  const match = url.match(/\/api\/([a-z0-9-_]+)/i);
  if (match && match[1]) {
    const formatted = match[1]
      .split(/[-_]/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return `Loading ${formatted}...`;
  }
  return null;
};

let _nextRequestId = 0;
const _inFlightRequests = new Map();
const _recentRequests = new Map();
const _requestListeners = new Set();

const _notifyRequestListeners = (event) => {
  _requestListeners.forEach(listener => {
    try {
      listener(event);
    } catch {
      // ignore listener errors
    }
  });
};

if (api && api.interceptors) {
  api.interceptors.request.use(
    (config) => {
      const label = getRequestLabel(config?.url || '');
      if (label) {
        const id = ++_nextRequestId;
        const entry = { id, label, url: config.url, startedAt: Date.now(), completed: false };
        config._loaderTrackId = id;
        _inFlightRequests.set(id, entry);
        _recentRequests.set(id, entry);
        _notifyRequestListeners({ type: 'start', entry });
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  const handleResponseEnd = (config) => {
    const id = config?._loaderTrackId;
    if (id && _inFlightRequests.has(id)) {
      const entry = _inFlightRequests.get(id);
      _inFlightRequests.delete(id);
      entry.completed = true;
      entry.completedAt = Date.now();
      _recentRequests.set(id, entry);
      _notifyRequestListeners({ type: 'end', entry });
    }
  };

  api.interceptors.response.use(
    (response) => {
      handleResponseEnd(response?.config);
      return response;
    },
    (error) => {
      handleResponseEnd(error?.config);
      return Promise.reject(error);
    }
  );
}

// Essential tasks - loaded BEFORE app renders (parallel batch)
// Keep this minimal — only data needed for the app shell (theming, departments).
// Everything else is deferred so the user sees the app immediately after login.
const ESSENTIAL_TASKS = [
  { key: 'organization', label: 'Organization', fetch: () => organizationAPI.getSettings(), category: 'setup' },
  { key: 'departments', label: 'Departments', fetch: () => departmentAPI.getAll(), category: 'setup' },
];

// Deferred tasks - loaded in background AFTER app renders (parallel batches)
const DEFERRED_TASKS = [
  // Batch A - High priority background
  { key: PATIENT_LIST_KEY, label: 'Patients', fetch: () => patientAPI.getAll({ limit: 1000 }), category: 'patients' },
  { key: 'appointments', label: 'Appointments', fetch: () => appointmentAPI.getAll({ limit: 200 }), category: 'patients' },
  { key: 'users', label: 'Staff Users', fetch: () => authAPI.getAllUsers({ limit: 500 }), category: 'setup' },
  { key: 'followups', label: 'Follow-ups', fetch: () => followupAPI.getAll({ limit: 500 }), category: 'patients' },
  { key: 'visits', label: 'Clinical Visits', fetch: () => clinicalAPI.getVisits({ limit: 500 }), category: 'clinical' },
  { key: 'diagnoses', label: 'Diagnosis Catalog', fetch: () => diagnosisAPI.getAll({ limit: 500 }), category: 'clinical' },
  { key: 'drugs', label: 'Pharmacy Drugs', fetch: () => pharmacyAPI.getDrugs({ limit: 1000 }), category: 'pharmacy' },
  { key: 'drugCatalog', label: 'Drug Catalog', fetch: () => pharmacyAPI.getDrugCatalog('', 500), category: 'pharmacy' },
  { key: 'prescriptions', label: 'Pending Prescriptions', fetch: () => pharmacyAPI.getPendingPrescriptions({ limit: 200 }), category: 'pharmacy' },
  { key: 'labTests', label: 'Lab Tests', fetch: () => laboratoryAPI.getTests({ limit: 500 }), category: 'laboratory' },
  { key: 'labOrders', label: 'Lab Orders', fetch: () => laboratoryAPI.getOrders({ limit: 10000 }), category: 'laboratory' },
  { key: 'services', label: 'Service Catalog', fetch: () => billingAPI.getServices({ limit: 500 }), category: 'billing' },
  { key: 'invoices', label: 'Invoices', fetch: () => billingAPI.getInvoices({ limit: 300 }), category: 'billing' },

  // Batch B - Medium priority background
  { key: 'wards', label: 'Wards', fetch: () => ipdAPI.getWards(), category: 'ipd' },
  { key: 'beds', label: 'Beds', fetch: () => ipdAPI.getBeds({ limit: 500 }), category: 'ipd' },
  { key: 'admissions', label: 'Admissions', fetch: () => ipdAPI.getAdmissions({ limit: 300 }), category: 'ipd' },
  { key: 'maternalStats', label: 'Maternal Stats', fetch: () => maternalAPI.getStats(), category: 'maternal' },
  { key: 'allPregnancies', label: 'Pregnancies', fetch: () => maternalAPI.getPregnancies(), category: 'maternal' },
  { key: 'children', label: 'Children', fetch: () => maternalAPI.getChildren(), category: 'maternal' },
  { key: 'ancVisits', label: 'ANC Visits', fetch: () => maternalAPI.getANCVisits(), category: 'maternal' },
  { key: 'deliveries', label: 'Deliveries', fetch: () => maternalAPI.getDeliveries(), category: 'maternal' },
  { key: 'postnatalVisits', label: 'Postnatal Visits', fetch: () => maternalAPI.getPostnatalVisits(), category: 'maternal' },
  { key: 'immunizations', label: 'Immunizations', fetch: () => maternalAPI.getImmunizations(), category: 'maternal' },

  // Batch C - Lower priority background
  { key: 'fpClients', label: 'FP Clients', fetch: () => familyPlanningAPI.getClients({ limit: 300 }), category: 'family_planning' },
  { key: 'fpMethods', label: 'FP Methods', fetch: () => familyPlanningAPI.getMethods(), category: 'family_planning' },
  { key: 'bloodDonors', label: 'Blood Donors', fetch: () => bloodBankAPI.getDonors({ limit: 300 }), category: 'blood_bank' },
  { key: 'bloodInventory', label: 'Blood Inventory', fetch: () => bloodBankAPI.getInventory({ limit: 300 }), category: 'blood_bank' },
  { key: 'triages', label: 'Triages', fetch: () => emergencyAPI.getTriages({ limit: 200 }), category: 'emergency' },
  { key: 'edRooms', label: 'ED Rooms', fetch: () => emergencyAPI.getRooms({}), category: 'emergency' },
  { key: 'emergencyVisits', label: 'Emergency Visits', fetch: () => emergencyAPI.getVisits({ limit: 500 }), category: 'emergency' },
  { key: 'emergencyStats', label: 'Emergency Stats', fetch: () => emergencyAPI.getStats(), category: 'emergency' },
  { key: 'radiologyModalities', label: 'Imaging Modalities', fetch: () => radiologyAPI.getModalities({}), category: 'radiology' },
  { key: 'radiologyStudies', label: 'Imaging Studies', fetch: () => radiologyAPI.getStudies({ limit: 500 }), category: 'radiology' },
  { key: 'proceduresCatalog', label: 'Procedure Catalog', fetch: () => proceduresAPI.getCatalog({ is_active: true }), category: 'procedures' },
  { key: 'theatres', label: 'Theatres', fetch: () => proceduresAPI.getTheatres({}), category: 'procedures' },
  { key: 'procedures', label: 'Procedure Orders', fetch: () => proceduresAPI.getOrders({ limit: 500 }), category: 'procedures' },
  { key: 'inventory', label: 'Inventory Items', fetch: () => inventoryAPI.getAllItems(), category: 'inventory' },
  { key: 'suppliers', label: 'Suppliers', fetch: () => inventoryAPI.getSuppliers({ limit: 200 }), category: 'inventory' },
  { key: 'warehouses', label: 'Warehouses', fetch: () => inventoryAPI.getWarehouses(), category: 'inventory' },
  { key: 'accounts', label: 'Accounts', fetch: () => financeAPI.getAccounts({ limit: 200 }), category: 'finance' },
];

// Cache storage key - increment version to invalidate all caches
const CACHE_VERSION = 'v3';
const getCacheKey = (tenantId) => `ospito_preloaded_data_${CACHE_VERSION}_${tenantId || 'global'}`;
const getCacheTimestampKey = (tenantId) => `ospito_preload_timestamp_${CACHE_VERSION}_${tenantId || 'global'}`;
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const InitialLoader = ({ children, onComplete }) => {
  const { user } = useAuth();
  const { hydrateFromPreload } = useDataCache();
  const tenantId = user?.tenant_id || 'global';
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState(() => {
    const active = Array.from(_inFlightRequests.values());
    return active.length > 0 ? active[active.length - 1].label : `Loading ${ESSENTIAL_TASKS[0].label}...`;
  });
  const [, setCompletedTasks] = useState([]);
  const [error] = useState(null);
  const abortRef = useRef(null);
  const trackedIdsRef = useRef(new Set());
  const completedIdsRef = useRef(new Set());
  const cycleIndexRef = useRef(0);

  const cacheKey = useMemo(() => getCacheKey(tenantId), [tenantId]);
  const cacheTimestampKey = useMemo(() => getCacheTimestampKey(tenantId), [tenantId]);

  // Check if cached data is still valid
  const isCacheValid = useCallback(() => {
    try {
      const timestamp = localStorage.getItem(cacheTimestampKey);
      if (!timestamp) return false;

      const cacheAge = Date.now() - parseInt(timestamp, 10);
      return cacheAge < CACHE_TTL;
    } catch {
      return false;
    }
  }, [cacheTimestampKey]);

  // Get cached data
  const getCachedData = useCallback(() => {
    try {
      const data = localStorage.getItem(cacheKey);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }, [cacheKey]);

  // Save data to cache
  const saveCachedData = useCallback((data) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(cacheTimestampKey, Date.now().toString());
    } catch (e) {
      console.warn('Failed to cache preloaded data:', e);
    }
  }, [cacheKey, cacheTimestampKey]);

  // Track real background API requests while the loader is visible so the
  // progress bar and status label reflect what is actually being loaded.
  useEffect(() => {
    if (!user || !isLoading) return;

    const now = Date.now();
    _recentRequests.forEach((entry, id) => {
      if (now - entry.startedAt < 3000) {
        trackedIdsRef.current.add(id);
        if (entry.completed) {
          completedIdsRef.current.add(id);
        }
      } else {
        _recentRequests.delete(id);
      }
    });
    _inFlightRequests.forEach((entry, id) => {
      trackedIdsRef.current.add(id);
    });

    const computeProgress = () => {
      const total = Math.max(trackedIdsRef.current.size, ESSENTIAL_TASKS.length);
      const done = completedIdsRef.current.size;
      const inFlight = _inFlightRequests.size;
      const rawPct = total > 0 ? Math.round(((done + (inFlight > 0 ? 0.25 : 0)) / total) * 100) : 0;
      setProgress(prev => Math.max(prev, Math.min(95, rawPct)));
    };

    const pickActiveLabel = () => {
      const activeLabels = Array.from(
        new Set(Array.from(_inFlightRequests.values()).map(e => e.label))
      );
      if (activeLabels.length > 0) {
        const idx = cycleIndexRef.current % activeLabels.length;
        cycleIndexRef.current += 1;
        setCurrentTask(activeLabels[idx]);
      }
    };

    pickActiveLabel();
    computeProgress();

    const handleRequestEvent = ({ type, entry }) => {
      trackedIdsRef.current.add(entry.id);
      if (type === 'start') {
        setCurrentTask(entry.label);
      } else if (type === 'end') {
        completedIdsRef.current.add(entry.id);
        const remaining = Array.from(_inFlightRequests.values());
        if (remaining.length > 0) {
          setCurrentTask(remaining[remaining.length - 1].label);
        } else {
          setCurrentTask(entry.label);
        }
      }
      computeProgress();
    };

    _requestListeners.add(handleRequestEvent);

    const interval = setInterval(() => {
      pickActiveLabel();
      if (_inFlightRequests.size > 0) {
        const total = Math.max(trackedIdsRef.current.size, ESSENTIAL_TASKS.length);
        const done = completedIdsRef.current.size;
        const ceiling = Math.min(92, Math.round(((done + 0.85) / total) * 100));
        setProgress(prev => (prev < ceiling ? prev + 2 : prev));
      }
    }, 250);

    return () => {
      _requestListeners.delete(handleRequestEvent);
      clearInterval(interval);
    };
  }, [user, isLoading]);

  // Run a batch of tasks in parallel, returning { key: data } map.
  // Accepts optional AbortSignal to cancel pending fetches.
  const runBatch = useCallback(async (tasks, signal, onTaskProgress) => {
    const results = await Promise.allSettled(tasks.map(t => {
      if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
      if (onTaskProgress) onTaskProgress('start', t);
      return Promise.resolve(t.fetch()).finally(() => {
        if (!signal?.aborted && onTaskProgress) {
          onTaskProgress('end', t);
        }
      });
    }));
    if (signal?.aborted) return {};
    const data = {};
    results.forEach((result, idx) => {
      data[tasks[idx].key] = result.status === 'fulfilled' ? (result.value?.data || result.value) : null;
      if (result.status === 'rejected') {
        console.warn(`Failed to preload ${tasks[idx].label}:`, result.reason?.message);
      }
    });
    return data;
  }, []);

  // Load all data - essential first (parallel), then deferred in background (parallel)
  const loadAllData = useCallback(async (signal) => {
    // Check cache first
    if (isCacheValid()) {
      const cachedData = getCachedData();
      if (cachedData && !signal?.aborted) {
        setProgress(100);
        setCurrentTask('Loading from cache...');
        hydrateFromPreload(cachedData);
        setIsLoading(false);
        if (onComplete) onComplete(cachedData);
        return;
      }
    }

    if (signal?.aborted) return;

    // Phase 1: Load essential data in parallel (blocks UI).
    // Wrapped in try/finally so the UI is ALWAYS unblocked once essential
    // data has been attempted — even if hydration throws (which would
    // otherwise skip setIsLoading(false) and leave the loader stuck).
    let essentialDone = 0;
    let essentialData = {};
    try {
      essentialData = await runBatch(ESSENTIAL_TASKS, signal, (phase, task) => {
        if (phase === 'start') {
          if (_inFlightRequests.size === 0) {
            setCurrentTask(`Loading ${task.label}...`);
          }
        } else if (phase === 'end') {
          essentialDone += 1;
          setCompletedTasks(prev => (prev.includes(task.key) ? prev : [...prev, task.key]));
          const pct = Math.round((essentialDone / ESSENTIAL_TASKS.length) * 85);
          setProgress(prev => Math.max(prev, pct));
          const remaining = Array.from(_inFlightRequests.values());
          if (remaining.length > 0) {
            setCurrentTask(remaining[remaining.length - 1].label);
          } else {
            const nextTask = ESSENTIAL_TASKS.find(t => t.key !== task.key);
            if (nextTask && essentialDone < ESSENTIAL_TASKS.length) {
              setCurrentTask(`Loading ${nextTask.label}...`);
            }
          }
        }
      });
      if (signal?.aborted) return;
      setProgress(100);
      setCurrentTask('Complete');
      try { hydrateFromPreload(essentialData); }
      catch (e) { console.warn('Preload hydration failed:', e); }
      await new Promise(resolve => setTimeout(resolve, 120));
    } catch (e) {
      console.warn('Essential preload failed:', e);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
    if (!signal?.aborted && onComplete) onComplete(essentialData);

    // Phase 2: Load deferred data in background (doesn't block UI)
    const BATCH_SIZE = 10;
    const allDeferredData = {};
    let deferredDone = 0;
    for (let i = 0; i < DEFERRED_TASKS.length; i += BATCH_SIZE) {
      if (signal?.aborted) return;
      const batch = DEFERRED_TASKS.slice(i, i + BATCH_SIZE);
      if (batch.length > 0) {
        setCurrentTask(`Loading ${batch[0].label}...`);
      }
      const batchData = await runBatch(batch, signal, (phase, task) => {
        if (phase === 'end') {
          deferredDone += 1;
          setCompletedTasks(prev => (prev.includes(task.key) ? prev : [...prev, task.key]));
          setCurrentTask(`Loading ${task.label}...`);
        }
      });
      Object.assign(allDeferredData, batchData);
    }

    if (signal?.aborted) return;

    // Merge deferred data into cache and context
    const fullData = { ...essentialData, ...allDeferredData };
    try { hydrateFromPreload(fullData); }
    catch (e) { console.warn('Deferred hydration failed:', e); }
    try { saveCachedData(fullData); }
    catch (e) { console.warn('Cache save failed:', e); }
    setProgress(100);
    setCurrentTask('Complete');
  }, [isCacheValid, getCachedData, saveCachedData, onComplete, hydrateFromPreload, runBatch]);

  // Start (or restart) the preload whenever the user or tenant changes.
  // Single effect with proper cleanup: on mount the load starts; on tenant
  // change the in-flight load is aborted and a fresh one starts; on unmount
  // everything is cancelled.
  //
  // BUG HISTORY: this used to be two separate effects. The [tenantId] effect
  // ran on the initial mount (every useEffect runs on mount) and aborted the
  // load that had JUST been started by the [user, loadAllData] effect — with
  // nothing ever restarting it. The aborted load returned early without
  // setIsLoading(false), leaving the loader stuck at 0% forever.
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    abortRef.current = controller;
    loadAllData(controller.signal);
    return () => {
      controller.abort();
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    };
  }, [user?.id, tenantId, loadAllData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net: never leave the loader visible for more than 45s, even if
  // something unexpected goes wrong with the preload (slow network, etc).
  // Pages render their own loading/empty states for any data that is missing.
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => setIsLoading(false), 45000);
    return () => clearTimeout(t);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Skip loader if already loaded or no user
  if (!user || !isLoading) {
    return children;
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900 z-10">
      <div className="flex flex-col items-center w-full max-w-sm px-8">
        {/* Compact Ospito+ Logo */}
        <div className="flex items-baseline mb-6">
          <span
            className="text-3xl md:text-4xl font-bold leading-none bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent tracking-tight"
            style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}
          >
            Ospito
          </span>
          <span
            className="text-xl md:text-2xl font-bold leading-none bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent -ml-0.5 -translate-y-1.5 inline-block"
            style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}
          >
            +
          </span>
        </div>

        {/* Progress Bar Container */}
        <div className="w-full mb-4">
          {/* Loading text */}
          <div className="text-center mb-3">
            <span className="text-sm text-gray-400 dark:text-gray-500 font-medium tracking-wide">
              loading {progress}%
            </span>
          </div>

          {/* Sleek Progress Bar */}
          <div className="relative w-full h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            {/* Animated gradient fill */}
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #2dd4bf, #14b8a6, #0891b2, #06b6d4)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s linear infinite',
              }}
            />

            {/* Circular indicator */}
            {progress > 0 && (
              <div
                className="absolute top-1/2 -translate-y-1/2 transition-all duration-300 ease-out"
                style={{ left: `calc(${Math.min(progress, 98)}% - 6px)` }}
              >
                <div className="w-3 h-3 rounded-full bg-white shadow-md border-2 border-teal-500 dark:border-teal-400" />
              </div>
            )}
          </div>
        </div>

        {/* Current task - minimal */}
        <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-full">
          {currentTask}
        </p>

        {/* Error display */}
        {error && (
          <div className="mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-500 dark:text-red-400 text-xs">{error}</p>
          </div>
        )}
      </div>

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

// Export utility to clear cache (useful when logging out). Clears all
// tenant-scoped preload entries matching the version prefix.
export const clearPreloadCache = () => {
  const prefix = `ospito_preloaded_data_${CACHE_VERSION}_`;
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(prefix) || key.startsWith(`ospito_preload_timestamp_${CACHE_VERSION}_`)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) { /* ignore */ }
};

// Export utility to get preloaded data for a specific tenant
export const getPreloadedData = (tenantId) => {
  try {
    const key = getCacheKey(tenantId);
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

export default InitialLoader;
